// kidVoice — playback of bundled voice clips that announce which
// menu / card / mode the kid just tapped. Counterpart to kidSfx (the
// synthesized whoosh / coin / sparkle library); these are recorded
// audio assets sitting at public/mp3/cat/<name>.mp3.
//
// Why split from kidSfx: synthesis is great for abstract ambient
// feedback (pop / tick / fanfare) but can't say "Pet!" or "Build!".
// Voice clips do the recognition / literacy lift — the kid who can't
// yet read "FOODS" still hears the word when they tap the tile, and
// the association builds. Same library can be swapped per-kid (TTS
// variant in their voice, a parent's recorded voice, etc.) without
// touching call sites.
//
// All clips share one shared HTMLAudio cache so re-tapping the same
// tab doesn't re-fetch. Calls are no-ops if iOS hasn't unlocked the
// audio yet (first user gesture handles it).
//
// Opt-out shares the kidSfx flag so muting one mutes both — kids /
// parents don't think about "voice vs sfx," it's just sound on/off.

const VOICE_FILES = {
  // Tabs (KidsTabBar)
  pet:           "/mp3/cat/Pet.mp3",
  foods:         "/mp3/cat/Foods.mp3",
  watch:         "/mp3/cat/Watch.mp3",
  // NOTE: no Mix.mp3 yet. The Craft (Mix) tab has no voice file —
  // gap flagged to the user. Until uploaded, the tab silently
  // plays no voice (the kidSfx pop tick still fires).

  // Card types (KidCardDeck / CardRevealOverlay / SendCards)
  super:         "/mp3/cat/Super.mp3",
  build:         "/mp3/cat/Build.mp3",
  boost:         "/mp3/cat/Boost.mp3",
  spark:         "/mp3/cat/Spark.mp3",
  // dairy is the DB key; the kid-facing label is "Protect" because
  // the card grants DEF. We map both aliases to the same file so a
  // caller using either spelling lands on the voice.
  dairy:         "/mp3/cat/Protect.mp3",
  protect:       "/mp3/cat/Protect.mp3",
  // NOTE: no Water.mp3 yet. The Water card (mana refill) has no
  // voice file — gap flagged.

  // Modes
  // URL-encode the space — WKWebView's audio loader treats raw
  // spaces as invalid characters and silently fails to fetch.
  "magic-color": "/mp3/cat/Magic%20Color.mp3",

  // Request-flow narration. Played by the kid's request CTA
  // (asked-a-parent) and the My Asks strip (is-getting-made when
  // a request's cook_session_id is set by the parent starting
  // cooking).
  "asked-a-parent":  "/mp3/cat/AskedAParent.mp3",
  "is-getting-made": "/mp3/cat/IsGettingMade.mp3",

  // ── Sentence fragments for narrate-step sequences ────────────────
  // Concatenate with a per-canonical voiceline (Eggwich.mp3 etc.) to
  // build dynamic announcements like:
  //   "Your" + "EGG BURGER!" + "is in the oven!"
  // Upload each file to public/mp3/cat/<filename>. Sequencer skips
  // missing entries gracefully (silent, no error).
  "your":            "/mp3/cat/Your.mp3",
  "are":             "/mp3/cat/Are.mp3",
  "is":              "/mp3/cat/Is.mp3",
  "is-in-the-oven":  "/mp3/cat/IsInTheOven.mp3",
  "are-in-the-oven": "/mp3/cat/AreinTheOven.mp3",
  "is-sizzling":     "/mp3/cat/IsSizzling.mp3",
  "is-mixing":       "/mp3/cat/IsMixing.mp3",
  "is-baking":       "/mp3/cat/IsBaking.mp3",
  "is-cooling":      "/mp3/cat/IsCooling.mp3",
  "is-heating-up":   "/mp3/cat/IsHeatingUp.mp3",
  "is-flipping":     "/mp3/cat/IsFlipping.mp3",
  "is-cutting":      "/mp3/cat/IsCutting.mp3",
  "is-stirring":     "/mp3/cat/IsStirring.mp3",
  "is-almost-done":  "/mp3/cat/IsAlmostDone.mp3",
  "is-ready":        "/mp3/cat/IsReady.mp3",
  "yum":             "/mp3/cat/Yum.mp3",
  "wash-hands":      "/mp3/cat/WashHands.mp3",
};

const cache = new Map();

function audioEnabled() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("mise.kidSfx.enabled") !== "false";
  } catch {
    return true;
  }
}

export function playKidVoice(name) {
  if (!audioEnabled()) return;
  const key = (name || "").toLowerCase();
  const path = VOICE_FILES[key];
  if (!path) {
    // Silent fallback — unknown key shouldn't break the UI. Log
    // once in dev so missing files are easy to spot.
    if (typeof window !== "undefined" && !window.__kidVoiceWarned?.[key]) {
      console.warn("[kidVoice] no clip for:", key);
      window.__kidVoiceWarned = window.__kidVoiceWarned || {};
      window.__kidVoiceWarned[key] = true;
    }
    return;
  }
  let audio = cache.get(key);
  if (!audio) {
    audio = new Audio(path);
    audio.preload = "auto";
    cache.set(key, audio);
  }
  // Restart on re-tap so consecutive taps re-announce instead of
  // queueing — kid taps Build twice, they hear "Build! Build!" not
  // a delayed second play.
  try {
    audio.currentTime = 0;
    const p = audio.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => { /* autoplay-blocked / pre-gesture; ignore */ });
    }
  } catch {
    /* iOS pre-gesture: ignore */
  }
}

// Play a sequence of voicelines back-to-back so the pet can
// construct dynamic sentences like:
//   playKidVoiceSequence(["your", { url: eggwichVoiceUrl }, "is-in-the-oven"])
//   → "Your" → "Egg Burger!" → "is in the oven!"
//
// Items can be:
//   • string key into VOICE_FILES (e.g. "your")
//   • { url: string } — a remote URL (canonical voiceline, etc.)
//   • { key: string } — same as plain string but explicit
//
// Each item plays only after the previous ENDS, with a small gap.
// Missing files / 404s skip silently so a half-recorded library
// still narrates the parts it has. Returns a Promise that resolves
// when the whole sequence finishes (or rejects when interrupted).
export async function playKidVoiceSequence(items, opts = {}) {
  if (!audioEnabled()) return;
  if (!Array.isArray(items) || items.length === 0) return;
  const gapMs = Number.isFinite(opts.gapMs) ? opts.gapMs : 90;
  for (const item of items) {
    let url = null;
    if (typeof item === "string") {
      url = VOICE_FILES[item.toLowerCase()] || null;
    } else if (item && typeof item === "object") {
      if (typeof item.url === "string") url = item.url;
      else if (typeof item.key === "string") url = VOICE_FILES[item.key.toLowerCase()] || null;
    }
    if (!url) continue;
    await new Promise((resolve) => {
      const a = cache.get(url) || new Audio(url);
      if (!cache.has(url)) {
        a.preload = "auto";
        cache.set(url, a);
      }
      const cleanup = () => {
        a.removeEventListener("ended", onEnd);
        a.removeEventListener("error", onErr);
      };
      const onEnd = () => { cleanup(); resolve(); };
      const onErr = () => { cleanup(); resolve(); }; // skip on failure
      a.addEventListener("ended", onEnd);
      a.addEventListener("error", onErr);
      try {
        a.currentTime = 0;
        const p = a.play();
        if (p && typeof p.catch === "function") {
          p.catch(() => { cleanup(); resolve(); });
        }
      } catch {
        cleanup();
        resolve();
      }
    });
    if (gapMs > 0) await new Promise((r) => setTimeout(r, gapMs));
  }
}

export function preloadKidVoices() {
  // Optional pre-warm — call after the first user gesture so the
  // cache is populated before the kid taps the first tile. Each
  // file is ~50KB; eight prewarms is ~400KB across the connection.
  if (typeof window === "undefined") return;
  for (const [key, path] of Object.entries(VOICE_FILES)) {
    if (cache.has(key)) continue;
    const a = new Audio(path);
    a.preload = "auto";
    cache.set(key, a);
  }
}
